import React, { useMemo } from "react";
import Header from "./Components/Header";
import { CLASS_BOOKING } from "../../assets/asset";
import { TrendingUp } from "lucide-react";
import SessionCard from "./Components/SessionCard";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import PageContainer from "./Components/PageContainer";
import MainContent from "./Components/MainContent";
import upcomingIcon from "../../assets/global/upcoming_icon.svg";
import { useQuery } from "@tanstack/react-query";
import { bookingService } from "../../services/booking.service";

const formatBookingDate = (value) => {
  if (!value) return "Date not provided";

  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) return String(value);

  return parsedDate.toLocaleDateString("en-AU", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};

const mapBookingToSessionCard = (booking, studentName) => ({
  id:
    booking?.id ||
    booking?.booking_id ||
    `${booking?.tutor?.id || booking?.tutor?.name || "tutor"}-${
      booking?.slot?.id || booking?.slot?.date || "slot"
    }`,
  tutorName: booking?.tutor?.name || "Tutor",
  image: booking?.tutor?.photoImage || "",
  date: formatBookingDate(booking?.slot?.date || booking?.slot?.start_time),
  time: booking?.slot?.time_label || "Time not provided",
  student: studentName || "Student",
  status: booking?.is_upcoming ? "Upcoming" : "Completed",
  personLabel: "Student",
});

const Dashboard = () => {
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.user);
  const displayName =
    user?.user_name ||
    user?.full_name ||
    [user?.first_name, user?.last_name].filter(Boolean).join(" ") ||
    "";
  const bookingsQuery = useQuery({
    queryKey: ["student-class-booking-dashboard"],
    queryFn: bookingService.getBookings,
    retry: false,
  });

  const bookingSummary = bookingsQuery.data?.summary || {
    upcoming: 0,
    past: 0,
    total: 0,
  };
  const allBookings = useMemo(
    () => [...(bookingsQuery.data?.upcoming || []), ...(bookingsQuery.data?.past || [])],
    [bookingsQuery.data?.past, bookingsQuery.data?.upcoming]
  );
  const tutorsWorkedWith = useMemo(() => {
    const tutorKeys = new Set();

    allBookings.forEach((booking) => {
      const tutorId = booking?.tutor?.id || booking?.tutor?.name;
      if (tutorId) {
        tutorKeys.add(String(tutorId));
      }
    });

    return tutorKeys.size;
  }, [allBookings]);
  const upcomingSessions = useMemo(
    () =>
      (bookingsQuery.data?.upcoming || []).map((booking) =>
        mapBookingToSessionCard(booking, displayName)
      ),
    [bookingsQuery.data?.upcoming, displayName]
  );
  const previewSessions = upcomingSessions.slice(0, 3);

  const headerProps = {
    heading: "Welcome",
    subheading: "Let's help you excel",
    name: displayName,
    subject: "Mathematics"
  };

  const handleBookTutor = () => {
    navigate("/classBooking/bookTutor");
  };

  const handleMyBookings = () => {
    navigate("/classBooking/myBookings");
  };

  return (
    <PageContainer>
      <div className="sticky top-0 z-50 bg-[#DFF2EB]/70 backdrop-blur-md">
        <Header {...headerProps} />
      </div>

      <MainContent>
        <div className="flex items-center justify-between gap-10">
          <div className="flex-1">
            <h1 className="text-5xl font-bold text-gray-900">
              Need extra help?
            </h1>

            <p className="text-lg mt-4 text-gray-600 max-w-xl leading-relaxed">
              Browse qualified Australian maths tutors and book a personalised
              one-on-one session with an expert teacher.
            </p>

            <button 
              className="mt-6 px-10 py-3 bg-white shadow-md rounded-full font-semibold border border-gray-300 hover:shadow-lg transition"
              onClick={handleBookTutor}
            >
              Book a Tutor
            </button>
          </div>

          <div className="flex-1 flex justify-end">
            <img
              src={CLASS_BOOKING.heroImage}
              alt="Hero Illustration"
              className="w-[420px] h-auto object-contain"
            />
          </div>
        </div>

        <div className="mt-16 flex gap-10 w-full">
          <div className="flex-1 bg-white rounded-3xl shadow-md p-8 flex items-center gap-6 border border-gray-100">
            <div className="w-16 h-16 rounded-full bg-[#D7DDFF] flex items-center justify-center">
              <img
                src={upcomingIcon}
                alt="Upcoming sessions"
                className="w-8 h-8 object-contain"
              />
            </div>

            <div className="flex flex-col">
              <p className="text-lg text-gray-700 font-medium">
                Upcoming sessions
              </p>
              <h2 className="text-4xl font-semibold text-gray-900">
                {bookingsQuery.isLoading ? "..." : bookingSummary.upcoming}
              </h2>
            </div>
          </div>

          <div className="flex-1 bg-white rounded-3xl shadow-md p-8 flex items-center gap-6 border border-gray-100">
            <div className="w-16 h-16 rounded-full bg-[#E8FFE8] flex items-center justify-center">
              <TrendingUp
                size={28}
                strokeWidth={2.4}
                className="text-green-600"
              />
            </div>

            <div className="flex flex-col">
              <p className="text-lg text-gray-700 font-medium">
                Tutors worked with
              </p>
              <h2 className="text-4xl font-semibold text-gray-900">
                {bookingsQuery.isLoading ? "..." : tutorsWorkedWith}
              </h2>
            </div>
          </div>
        </div>

        <div className="bg-transparent mt-16 p-10">
          <div className="flex justify-between items-center mb-10">
            <h3 className="text-2xl font-semibold text-gray-900">
              Upcoming Sessions ({bookingsQuery.isLoading ? "..." : bookingSummary.upcoming})
            </h3>
            <button 
              className="text-blue-700 font-medium hover:underline"
              onClick={handleMyBookings}
            >
              See all my bookings
            </button>
          </div>

          <div className="flex flex-col gap-8">
            {bookingsQuery.isError ? (
              <div className="rounded-3xl border border-red-200 bg-red-50 px-6 py-5 text-red-700">
                {bookingsQuery.error?.message || "Unable to load bookings right now."}
              </div>
            ) : bookingsQuery.isLoading ? (
              <div className="rounded-3xl border border-gray-200 bg-white px-6 py-5 text-gray-600">
                Loading bookings...
              </div>
            ) : previewSessions.length ? (
              previewSessions.map((session) => (
                <SessionCard key={session.id} session={session} />
              ))
            ) : (
              <div className="rounded-3xl border border-gray-200 bg-white px-6 py-5 text-gray-600">
                No upcoming bookings yet.
              </div>
            )}
          </div>
        </div>
      </MainContent>
    </PageContainer>
  );
};

export default Dashboard;
